import type { Bar } from '../chart/chartModel';
import { C, applied, int, method, num, price, type IndicatorDef, type OutputDef, type Params } from './defs';
import { ema, highest, lowest, movingAverage, sma, smma, type MaMethod, type Series } from './math';

// The rest of MT5's standard indicators, each following the formula of MT5's own source (MQL5\Indicators\Examples), so the
// values should agree bar for bar once the warm-up is over. Where MT5 seeds a smoothing differently from the textbook, the
// comment says which one is used.

const GREEN = '#26a69a';
const RED = '#ef5350';
const BW = { green: '#26a69a', brown: '#8d6e63', blue: '#4aa3ff', pink: '#ff5fa2' };

const v = (s: Series, i: number): number | null => {
  const x = s[i];
  return x === null || x === undefined ? null : x;
};

/** An EMA that starts at `begin` (MT5's ExponentialMAOnBuffer with a begin offset): seeded with the value there. */
function emaFrom(values: Series, period: number, begin: number): Series {
  return ema(
    values.map((x, i) => (i < begin ? null : x)),
    period,
  );
}

/** Green when the value rose (or held) against the previous bar, red when it fell: MT5's two-colour histograms. */
const risingIndex =
  (key: string) =>
  (computed: Record<string, Series>, i: number): number => {
    const now = v(computed[key]!, i);
    const before = v(computed[key]!, i - 1);
    return now !== null && before !== null && now < before ? 1 : 0;
  };

/** Same, on the size of the value (the Gator's lower histogram is negative, and grows downwards). */
const growingIndex =
  (key: string) =>
  (computed: Record<string, Series>, i: number): number => {
    const now = v(computed[key]!, i);
    const before = v(computed[key]!, i - 1);
    return now !== null && before !== null && Math.abs(now) < Math.abs(before) ? 1 : 0;
  };

const twoColour = (key: string, label: string, index = risingIndex(key)): OutputDef => ({
  key,
  label,
  style: 'histogram',
  color: GREEN,
  palette: [GREEN, RED],
  paletteIndex: index,
});

/** Accumulation/Distribution (MT5's AD.mq5): the close's place in the bar's range, times the tick volume, added up. */
export function accumulationDistribution(bars: Bar[]): number[] {
  let total = 0;
  return bars.map((b) => {
    const range = b.h - b.l;
    const clv = b.c - b.l - (b.h - b.c);
    total += clv !== 0 && range !== 0 ? (clv / range) * b.tv : 0;
    return total;
  });
}

/** Kaufman's Adaptive Moving Average (MT5's AMA.mq5). */
export function ama(values: Series, period: number, fast: number, slow: number): Series {
  const fastSc = 2 / (fast + 1);
  const slowSc = 2 / (slow + 1);
  const out: Series = values.map(() => null);
  let prev: number | null = null;
  for (let i = period - 1; i < values.length; i++) {
    const x = v(values, i);
    if (x === null) continue;
    if (prev === null) {
      prev = x;
      out[i] = prev;
      continue;
    }
    const back = v(values, i - period);
    if (back === null) continue;
    let noise = 0;
    let ok = true;
    for (let k = 0; k < period; k++) {
      const a = v(values, i - k);
      const b = v(values, i - k - 1);
      if (a === null || b === null) {
        ok = false;
        break;
      }
      noise += Math.abs(a - b);
    }
    if (!ok) continue;
    const er = noise === 0 ? 0 : Math.abs(x - back) / noise;
    const ssc = (er * (fastSc - slowSc) + slowSc) ** 2;
    prev = prev + ssc * (x - prev);
    out[i] = prev;
  }
  return out;
}

/** Fractal Adaptive Moving Average (MT5's FrAMA.mq5): the fractal dimension of the highs and lows sets the smoothing. */
export function frama(bars: Bar[], values: Series, period: number): Series {
  const highs = bars.map((b) => b.h);
  const lows = bars.map((b) => b.l);
  const out: Series = values.map(() => null);
  const start = 2 * period - 1;
  if (bars.length < start) return out;
  let prev = v(values, start - 1);
  out[start - 1] = prev;
  for (let i = start; i < bars.length; i++) {
    const x = v(values, i);
    if (x === null || prev === null) {
      prev = x;
      out[i] = x;
      continue;
    }
    const n1 = (highest(highs, i, period) - lowest(lows, i, period)) / period;
    const n2 = (highest(highs, i - period, period) - lowest(lows, i - period, period)) / period;
    const n3 = (highest(highs, i, 2 * period) - lowest(lows, i, 2 * period)) / (2 * period);
    // A flat stretch has no dimension: follow the price.
    const alpha =
      n1 + n2 > 0 && n3 > 0 ? Math.exp(-4.6 * ((Math.log(n1 + n2) - Math.log(n3)) / Math.log(2) - 1)) : 1;
    const a = Math.min(1, Math.max(0.01, alpha));
    prev = a * x + (1 - a) * prev;
    out[i] = prev;
  }
  return out;
}

/** Variable Index Dynamic Average (MT5's VIDYA.mq5): an EMA whose speed follows the Chande Momentum Oscillator. */
export function vidya(values: Series, cmoPeriod: number, emaPeriod: number): Series {
  const f = 2 / (emaPeriod + 1);
  const out: Series = values.map(() => null);
  let prev: number | null = null;
  for (let i = cmoPeriod; i < values.length; i++) {
    const x = v(values, i);
    if (x === null) continue;
    let up = 0;
    let down = 0;
    let ok = true;
    for (let k = 0; k < cmoPeriod; k++) {
      const a = v(values, i - k);
      const b = v(values, i - k - 1);
      if (a === null || b === null) {
        ok = false;
        break;
      }
      if (a > b) up += a - b;
      else down += b - a;
    }
    if (!ok) continue;
    if (prev === null) {
      prev = x;
      out[i] = prev;
      continue;
    }
    const cmo = up + down === 0 ? 0 : Math.abs((up - down) / (up + down));
    prev = x * f * cmo + prev * (1 - f * cmo);
    out[i] = prev;
  }
  return out;
}

/** The ADX as J. Welles Wilder defined it (MT5's "ADX Wilder"): Wilder's smoothing of TR, +DM and -DM, then of DX. */
export function adxWilder(bars: Bar[], n: number): { adx: Series; plus: Series; minus: Series } {
  const tr: Series = [null];
  const pdm: Series = [null];
  const ndm: Series = [null];
  for (let i = 1; i < bars.length; i++) {
    const b = bars[i]!;
    const prev = bars[i - 1]!;
    let up = b.h - prev.h;
    let down = prev.l - b.l;
    if (up < 0) up = 0;
    if (down < 0) down = 0;
    if (up > down) down = 0;
    else if (up < down) up = 0;
    else up = down = 0;
    tr.push(Math.max(b.h, prev.c) - Math.min(b.l, prev.c));
    pdm.push(up);
    ndm.push(down);
  }
  const atr = smma(tr, n);
  const sp = smma(pdm, n);
  const sn = smma(ndm, n);
  const plus: Series = atr.map((a, i) =>
    a === null || sp[i] === null ? null : a === 0 ? 0 : (100 * sp[i]!) / a,
  );
  const minus: Series = atr.map((a, i) =>
    a === null || sn[i] === null ? null : a === 0 ? 0 : (100 * sn[i]!) / a,
  );
  const dx: Series = plus.map((pv, i) => {
    const mv = minus[i];
    if (pv === null || mv === null || mv === undefined) return null;
    return pv + mv === 0 ? 0 : (100 * Math.abs(pv - mv)) / (pv + mv);
  });
  return { adx: smma(dx, n), plus, minus };
}

/** Relative Vigor Index (MT5's RVI.mq5): the close-open move against the range, both weighted 1-2-2-1 over four bars. */
export function rvi(bars: Bar[], period: number): { rvi: Series; signal: Series } {
  const co = (i: number) => bars[i]!.c - bars[i]!.o;
  const hl = (i: number) => bars[i]!.h - bars[i]!.l;
  const w = (f: (i: number) => number, i: number) => (f(i) + 2 * f(i - 1) + 2 * f(i - 2) + f(i - 3)) / 6;
  const main: Series = bars.map((_, i) => {
    if (i < period + 2) return null;
    let a = 0;
    let b = 0;
    for (let k = i - period + 1; k <= i; k++) {
      a += w(co, k);
      b += w(hl, k);
    }
    return b === 0 ? a : a / b;
  });
  const signal: Series = main.map((_, i) => {
    const [a, b, c, d] = [v(main, i), v(main, i - 1), v(main, i - 2), v(main, i - 3)];
    return a === null || b === null || c === null || d === null ? null : (a + 2 * b + 2 * c + d) / 6;
  });
  return { rvi: main, signal };
}

/** Bill Williams' Alligator lines, before their shift: jaws, teeth and lips are smoothed averages of the median price. */
function alligatorLines(bars: Bar[], p: Params) {
  const median = price(bars, { applied: 'median' });
  const m = p.method as MaMethod;
  return {
    jaws: movingAverage(median, num(p, 'jawPeriod'), m),
    teeth: movingAverage(median, num(p, 'teethPeriod'), m),
    lips: movingAverage(median, num(p, 'lipsPeriod'), m),
  };
}

const alligatorParams = [
  int('jawPeriod', 'Jaws period', 13),
  int('jawShift', 'Jaws shift', 8, 0, 100),
  int('teethPeriod', 'Teeth period', 8),
  int('teethShift', 'Teeth shift', 5, 0, 100),
  int('lipsPeriod', 'Lips period', 5),
  int('lipsShift', 'Lips shift', 3, 0, 100),
  method('method', 'Method', 'smma'),
];

/** Awesome Oscillator: SMA(5) - SMA(34) of the median price. */
function awesome(bars: Bar[]): Series {
  const median = price(bars, { applied: 'median' });
  const fast = sma(median, 5);
  const slow = sma(median, 34);
  return fast.map((f, i) => (f === null || slow[i] === null ? null : f - slow[i]!));
}

const shifted = (series: Series, by: number): Series => series.map((_, i) => v(series, i - by));

export const EXTRA_INDICATORS: IndicatorDef[] = [
  // ------------------------------------------------------------------ trend
  {
    id: 'ama',
    name: 'Adaptive Moving Average',
    group: 'Trend',
    overlay: true,
    sourceable: true,
    params: [
      int('period', 'Period', 10),
      int('fast', 'Fast EMA', 2),
      int('slow', 'Slow EMA', 30),
      applied('applied', 'Apply to'),
    ],
    outputs: [{ key: 'ama', label: 'AMA', style: 'line', color: C.cyan }],
    compute: (bars, p, source) => ({
      ama: ama(price(bars, p, 'applied', source), num(p, 'period'), num(p, 'fast'), num(p, 'slow')),
    }),
    title: (p) => `AMA (${p.period}, ${p.fast}, ${p.slow})`,
  },
  {
    id: 'dema',
    name: 'Double Exponential Moving Average',
    group: 'Trend',
    overlay: true,
    sourceable: true,
    params: [int('period', 'Period', 14), applied('applied', 'Apply to')],
    outputs: [{ key: 'dema', label: 'DEMA', style: 'line', color: C.amber }],
    compute: (bars, p, source) => {
      const n = num(p, 'period');
      const e1 = ema(price(bars, p, 'applied', source), n);
      const e2 = emaFrom(e1, n, n - 1);
      return { dema: e1.map((a, i) => (a === null || e2[i] === null ? null : 2 * a - e2[i]!)) };
    },
    title: (p) => `DEMA (${p.period})`,
  },
  {
    id: 'tema',
    name: 'Triple Exponential Moving Average',
    group: 'Trend',
    overlay: true,
    sourceable: true,
    params: [int('period', 'Period', 14), applied('applied', 'Apply to')],
    outputs: [{ key: 'tema', label: 'TEMA', style: 'line', color: C.violet }],
    compute: (bars, p, source) => {
      const n = num(p, 'period');
      const e1 = ema(price(bars, p, 'applied', source), n);
      const e2 = emaFrom(e1, n, n - 1);
      const e3 = emaFrom(e2, n, 2 * n - 2);
      return {
        tema: e1.map((a, i) =>
          a === null || e2[i] === null || e3[i] === null ? null : 3 * a - 3 * e2[i]! + e3[i]!,
        ),
      };
    },
    title: (p) => `TEMA (${p.period})`,
  },
  {
    id: 'frama',
    name: 'Fractal Adaptive Moving Average',
    group: 'Trend',
    overlay: true,
    params: [int('period', 'Period', 14), applied('applied', 'Apply to')],
    outputs: [{ key: 'frama', label: 'FrAMA', style: 'line', color: C.pink }],
    compute: (bars, p) => ({ frama: frama(bars, price(bars, p), num(p, 'period')) }),
    title: (p) => `FrAMA (${p.period})`,
  },
  {
    id: 'vidya',
    name: 'Variable Index Dynamic Average',
    group: 'Trend',
    overlay: true,
    sourceable: true,
    params: [int('cmo', 'CMO period', 9), int('ema', 'EMA period', 12), applied('applied', 'Apply to')],
    outputs: [{ key: 'vidya', label: 'VIDYA', style: 'line', color: C.green }],
    compute: (bars, p, source) => ({
      vidya: vidya(price(bars, p, 'applied', source), num(p, 'cmo'), num(p, 'ema')),
    }),
    title: (p) => `VIDYA (${p.cmo}, ${p.ema})`,
  },
  {
    id: 'adxw',
    name: 'Average Directional Index Wilder',
    group: 'Trend',
    overlay: false,
    levels: [25],
    params: [int('period', 'Period', 14)],
    outputs: [
      { key: 'adx', label: 'ADX Wilder', style: 'line', color: C.grey },
      { key: 'plus', label: '+DI', style: 'line', color: C.green },
      { key: 'minus', label: '-DI', style: 'line', color: C.red },
    ],
    compute: (bars, p) => ({ ...adxWilder(bars, num(p, 'period')) }),
    title: (p) => `ADX Wilder (${p.period})`,
  },
  // ------------------------------------------------------------------ oscillators
  {
    id: 'bears',
    name: 'Bears Power',
    group: 'Oscillators',
    overlay: false,
    levels: [0],
    params: [int('period', 'Period', 13)],
    outputs: [{ key: 'bears', label: 'Bears', style: 'histogram', color: C.grey }],
    compute: (bars, p) => {
      const e = ema(
        bars.map((b) => b.c),
        num(p, 'period'),
      );
      return { bears: bars.map((b, i) => (e[i] === null ? null : b.l - e[i]!)) };
    },
    title: (p) => `Bears (${p.period})`,
  },
  {
    id: 'bulls',
    name: 'Bulls Power',
    group: 'Oscillators',
    overlay: false,
    levels: [0],
    params: [int('period', 'Period', 13)],
    outputs: [{ key: 'bulls', label: 'Bulls', style: 'histogram', color: C.grey }],
    compute: (bars, p) => {
      const e = ema(
        bars.map((b) => b.c),
        num(p, 'period'),
      );
      return { bulls: bars.map((b, i) => (e[i] === null ? null : b.h - e[i]!)) };
    },
    title: (p) => `Bulls (${p.period})`,
  },
  {
    id: 'chaikin',
    name: 'Chaikin Oscillator',
    group: 'Oscillators',
    overlay: false,
    levels: [0],
    params: [int('fast', 'Fast MA', 3), int('slow', 'Slow MA', 10), method('method', 'Method', 'ema')],
    outputs: [{ key: 'cho', label: 'CHO', style: 'line', color: C.cyan }],
    compute: (bars, p) => {
      const ad = accumulationDistribution(bars);
      const m = p.method as MaMethod;
      const fast = movingAverage(ad, num(p, 'fast'), m);
      const slow = movingAverage(ad, num(p, 'slow'), m);
      return { cho: fast.map((f, i) => (f === null || slow[i] === null ? null : f - slow[i]!)) };
    },
    title: (p) => `Chaikin (${p.fast}, ${p.slow})`,
  },
  {
    id: 'demarker',
    name: 'DeMarker',
    group: 'Oscillators',
    overlay: false,
    levels: [0.3, 0.7],
    params: [int('period', 'Period', 14)],
    outputs: [{ key: 'dem', label: 'DeMarker', style: 'line', color: C.blue }],
    compute: (bars, p) => {
      const n = num(p, 'period');
      const max: Series = bars.map((b, i) => (i === 0 ? null : Math.max(b.h - bars[i - 1]!.h, 0)));
      const min: Series = bars.map((b, i) => (i === 0 ? null : Math.max(bars[i - 1]!.l - b.l, 0)));
      const a = sma(max, n);
      const b = sma(min, n);
      return {
        dem: a.map((x, i) => (x === null || b[i] === null ? null : x + b[i]! === 0 ? 0 : x / (x + b[i]!))),
      };
    },
    title: (p) => `DeMarker (${p.period})`,
  },
  {
    id: 'force',
    name: 'Force Index',
    group: 'Oscillators',
    overlay: false,
    levels: [0],
    params: [int('period', 'Period', 13), method('method', 'Method'), applied('applied', 'Apply to')],
    outputs: [{ key: 'force', label: 'Force', style: 'line', color: C.amber }],
    // MT5's Force.mq5: the tick volume times the change of a moving average of the price.
    compute: (bars, p) => {
      const ma = movingAverage(price(bars, p), num(p, 'period'), p.method as MaMethod);
      return {
        force: bars.map((b, i) => {
          const now = v(ma, i);
          const before = v(ma, i - 1);
          return now === null || before === null ? null : b.tv * (now - before);
        }),
      };
    },
    title: (p) => `Force (${p.period})`,
  },
  {
    id: 'osma',
    name: 'Moving Average of Oscillator',
    group: 'Oscillators',
    overlay: false,
    levels: [0],
    params: [
      int('fast', 'Fast EMA', 12),
      int('slow', 'Slow EMA', 26),
      int('signal', 'Signal SMA', 9),
      applied('applied', 'Apply to'),
    ],
    outputs: [{ key: 'osma', label: 'OsMA', style: 'histogram', color: C.grey }],
    compute: (bars, p) => {
      const values = price(bars, p);
      const fast = ema(values, num(p, 'fast'));
      const slow = ema(values, num(p, 'slow'));
      const macd: Series = fast.map((f, i) => (f === null || slow[i] === null ? null : f - slow[i]!));
      const signal = sma(macd, num(p, 'signal'));
      return { osma: macd.map((m, i) => (m === null || signal[i] === null ? null : m - signal[i]!)) };
    },
    title: (p) => `OsMA (${p.fast}, ${p.slow}, ${p.signal})`,
  },
  {
    id: 'rvi',
    name: 'Relative Vigor Index',
    group: 'Oscillators',
    overlay: false,
    levels: [0],
    params: [int('period', 'Period', 10)],
    outputs: [
      { key: 'rvi', label: 'RVI', style: 'line', color: C.green },
      { key: 'signal', label: 'Signal', style: 'line', color: C.red },
    ],
    compute: (bars, p) => ({ ...rvi(bars, num(p, 'period')) }),
    title: (p) => `RVI (${p.period})`,
  },
  {
    id: 'trix',
    name: 'Triple Exponential Average',
    group: 'Oscillators',
    overlay: false,
    levels: [0],
    sourceable: true,
    params: [int('period', 'Period', 14), applied('applied', 'Apply to')],
    outputs: [{ key: 'trix', label: 'TRIX', style: 'line', color: C.pink }],
    // MT5's TRIX.mq5: the bar-to-bar rate of change of a triple EMA (a fraction, not a percentage).
    compute: (bars, p, source) => {
      const n = num(p, 'period');
      const e1 = ema(price(bars, p, 'applied', source), n);
      const e2 = emaFrom(e1, n, n - 1);
      const e3 = emaFrom(e2, n, 2 * n - 2);
      return {
        trix: e3.map((x, i) => {
          const before = v(e3, i - 1);
          return x === null || before === null || before === 0 ? null : (x - before) / before;
        }),
      };
    },
    title: (p) => `TRIX (${p.period})`,
  },
  // ------------------------------------------------------------------ volumes
  {
    id: 'ad',
    name: 'Accumulation/Distribution',
    group: 'Volumes',
    overlay: false,
    params: [],
    outputs: [{ key: 'ad', label: 'A/D', style: 'line', color: C.cyan }],
    compute: (bars) => ({ ad: accumulationDistribution(bars) }),
    title: () => 'A/D',
  },
  {
    id: 'mfi',
    name: 'Money Flow Index',
    group: 'Volumes',
    overlay: false,
    levels: [20, 80],
    params: [int('period', 'Period', 14)],
    outputs: [{ key: 'mfi', label: 'MFI', style: 'line', color: C.blue }],
    compute: (bars, p) => {
      const n = num(p, 'period');
      const tp = bars.map((b) => (b.h + b.l + b.c) / 3);
      return {
        mfi: bars.map((_, i) => {
          if (i < n) return null;
          let pos = 0;
          let neg = 0;
          for (let k = i - n + 1; k <= i; k++) {
            if (tp[k]! > tp[k - 1]!) pos += tp[k]! * bars[k]!.tv;
            else if (tp[k]! < tp[k - 1]!) neg += tp[k]! * bars[k]!.tv;
          }
          return neg === 0 ? 100 : 100 - 100 / (1 + pos / neg);
        }),
      };
    },
    title: (p) => `MFI (${p.period})`,
  },
  {
    id: 'obv',
    name: 'On Balance Volume',
    group: 'Volumes',
    overlay: false,
    params: [],
    outputs: [{ key: 'obv', label: 'OBV', style: 'line', color: C.amber }],
    compute: (bars) => {
      let total = 0;
      return {
        obv: bars.map((b, i) => {
          if (i === 0) total = b.tv;
          else if (b.c > bars[i - 1]!.c) total += b.tv;
          else if (b.c < bars[i - 1]!.c) total -= b.tv;
          return total;
        }),
      };
    },
    title: () => 'OBV',
  },
  // ------------------------------------------------------------------ Bill Williams
  {
    id: 'alligator',
    name: 'Alligator',
    group: 'Bill Williams',
    overlay: true,
    params: alligatorParams,
    outputs: [
      { key: 'jaws', label: 'Jaws', style: 'line', color: C.blue, shift: (p) => num(p, 'jawShift') },
      { key: 'teeth', label: 'Teeth', style: 'line', color: C.red, shift: (p) => num(p, 'teethShift') },
      { key: 'lips', label: 'Lips', style: 'line', color: C.green, shift: (p) => num(p, 'lipsShift') },
    ],
    compute: (bars, p) => alligatorLines(bars, p),
    title: (p) => `Alligator (${p.jawPeriod}, ${p.teethPeriod}, ${p.lipsPeriod})`,
  },
  {
    id: 'fractals',
    name: 'Fractals',
    group: 'Bill Williams',
    overlay: true,
    params: [],
    outputs: [
      { key: 'up', label: 'Up fractal', style: 'dots', color: C.green },
      { key: 'down', label: 'Down fractal', style: 'dots', color: C.red },
    ],
    // MT5's Fractals.mq5: a high above the two bars after it and not below the two before it (lows the other way round).
    // The newest two bars cannot be a fractal yet.
    compute: (bars) => {
      const up: Series = bars.map(() => null);
      const down: Series = bars.map(() => null);
      for (let i = 2; i < bars.length - 2; i++) {
        const [a, b, x, c, d] = [bars[i - 2]!, bars[i - 1]!, bars[i]!, bars[i + 1]!, bars[i + 2]!];
        if (x.h > c.h && x.h > d.h && x.h >= b.h && x.h >= a.h) up[i] = x.h;
        if (x.l < c.l && x.l < d.l && x.l <= b.l && x.l <= a.l) down[i] = x.l;
      }
      return { up, down };
    },
    title: () => 'Fractals',
  },
  {
    id: 'gator',
    name: 'Gator Oscillator',
    group: 'Bill Williams',
    overlay: false,
    levels: [0],
    params: alligatorParams,
    outputs: [
      { ...twoColour('upper', 'Upper', growingIndex('upper')), shift: (p) => num(p, 'teethShift') },
      { ...twoColour('lower', 'Lower', growingIndex('lower')), shift: (p) => num(p, 'lipsShift') },
    ],
    // The distance between the Alligator's lines as drawn: |jaws - teeth| above zero, -|teeth - lips| below.
    compute: (bars, p) => {
      const { jaws, teeth, lips } = alligatorLines(bars, p);
      const j = shifted(jaws, num(p, 'jawShift') - num(p, 'teethShift'));
      const t = shifted(teeth, num(p, 'teethShift') - num(p, 'lipsShift'));
      return {
        upper: teeth.map((x, i) => (x === null || j[i] === null ? null : Math.abs(j[i]! - x))),
        lower: lips.map((x, i) => (x === null || t[i] === null ? null : -Math.abs(t[i]! - x))),
      };
    },
    title: (p) => `Gator (${p.jawPeriod}, ${p.teethPeriod}, ${p.lipsPeriod})`,
  },
  {
    id: 'ao',
    name: 'Awesome Oscillator',
    group: 'Bill Williams',
    overlay: false,
    levels: [0],
    params: [],
    outputs: [twoColour('ao', 'AO')],
    compute: (bars) => ({ ao: awesome(bars) }),
    title: () => 'AO',
  },
  {
    id: 'ac',
    name: 'Accelerator Oscillator',
    group: 'Bill Williams',
    overlay: false,
    levels: [0],
    params: [],
    outputs: [twoColour('ac', 'AC')],
    compute: (bars) => {
      const ao = awesome(bars);
      const avg = sma(ao, 5);
      return { ac: ao.map((x, i) => (x === null || avg[i] === null ? null : x - avg[i]!)) };
    },
    title: () => 'AC',
  },
  {
    id: 'bwmfi',
    name: 'Market Facilitation Index',
    group: 'Bill Williams',
    overlay: false,
    params: [],
    outputs: [
      {
        key: 'mfi',
        label: 'BW MFI',
        style: 'histogram',
        color: BW.green,
        palette: [BW.green, BW.brown, BW.blue, BW.pink],
        // Green: the index and the volume rose. Brown ("fade"): both fell. Blue ("fake"): index up, volume down.
        // Pink ("squat"): index down, volume up.
        paletteIndex: (computed, i, bars) => {
          const now = v(computed.mfi!, i);
          const before = v(computed.mfi!, i - 1);
          if (now === null || before === null || i === 0) return 0;
          const mfiUp = now > before;
          const volUp = bars[i]!.tv > bars[i - 1]!.tv;
          return mfiUp && volUp ? 0 : !mfiUp && !volUp ? 1 : mfiUp ? 2 : 3;
        },
      },
    ],
    compute: (bars) => ({ mfi: bars.map((b) => (b.tv === 0 ? null : (b.h - b.l) / b.tv)) }),
    title: () => 'BW MFI',
  },
];
